/**
 * Presentation contract "ui v2" (split-slider@3.2.0 onwards): the Figma editor cards.
 *
 *   ui.groups     cards (Brand, Layout Design, Content) and `placement: "modal"` groups that
 *                 live in Campaign Setting instead of a card; `more` adds Show More / Show Less
 *   ui.subgroups  blocks inside a card: `panel` (the Identity panel), `collapse`
 *                 ("Customize Buttons ^"), `tabs` (extra element styles)
 *   field.ui      group, subgroup, tab, more, order, width, control, hidden, hiddenOptions,
 *                 label (short caption), placeholder(s), heading
 *
 * The builder only arranges fields; every field keeps its declared type, validation and
 * visibility rules. Fields the design removed carry `ui.hidden` and are reported in
 * `hidden` so a server error on one can still be shown.
 */

function order(node) {
  const value = node?.ui?.order
  return typeof value === 'number' ? value : Number.MAX_SAFE_INTEGER
}

export function isUiV2(schema) {
  return schema?.ui?.version === 2
}

/**
 * Turns an ordered field list into render blocks: consecutive fields of one subgroup become
 * one block (placed where its first field sits), everything else stays a single field.
 */
export function blocksFor(fields, subgroups) {
  const blocks = []
  const byId = new Map()
  for (const field of fields) {
    const id = field.ui?.subgroup
    const subgroup = id ? subgroups.get(id) : null
    if (!subgroup) {
      blocks.push({ kind: 'field', key: field.path, field })
      continue
    }
    let block = byId.get(id)
    if (!block) {
      block = { kind: 'subgroup', key: `subgroup:${id}`, subgroup, fields: [] }
      byId.set(id, block)
      blocks.push(block)
    }
    block.fields.push(field)
  }
  return blocks
}

export function buildUiLayoutV2(schema) {
  if (!isUiV2(schema)) return null
  const ui = schema.ui
  const groups = (ui.groups || []).map((group) => ({ ...group, fields: [], more: [], sections: [] }))
  const byId = new Map(groups.map((group) => [group.id, group]))
  const subgroups = new Map((ui.subgroups || []).map((subgroup) => [subgroup.id, subgroup]))
  const hidden = []
  const leftovers = []

  for (const section of schema.sections || []) {
    if (section.collection) {
      const target = byId.get(section.ui?.group)
      if (target) target.sections.push(section)
      else leftovers.push({ kind: 'section', section })
      continue
    }
    for (const field of section.fields || []) {
      if (field.ui?.hidden) {
        hidden.push(field)
        continue
      }
      const target = byId.get(field.ui?.group)
      if (!target) {
        leftovers.push({ kind: 'field', field })
        continue
      }
      ;(field.ui?.more ? target.more : target.fields).push(field)
    }
  }

  for (const group of groups) {
    group.fields.sort((a, b) => order(a) - order(b))
    group.more.sort((a, b) => order(a) - order(b))
    group.sections.sort((a, b) => order(a) - order(b))
    group.blocks = blocksFor(group.fields, subgroups)
    group.moreBlocks = blocksFor(group.more, subgroups)
  }

  return {
    cards: groups.filter((group) => group.placement !== 'modal'
      && (group.fields.length || group.more.length || group.sections.length)),
    modal: groups.filter((group) => group.placement === 'modal'),
    subgroups,
    hidden,
    leftovers,
  }
}

/**
 * Options a control offers: `ui.hiddenOptions` are left out unless one is the current value
 * (an AI or preset choice the manual UI no longer offers must still read correctly).
 */
export function offeredOptions(field, options, value) {
  const hiddenValues = field?.ui?.hiddenOptions || []
  return (options || field?.options || []).filter(
    (option) => !hiddenValues.includes(option.value) || option.value === value,
  )
}

/** The caption above a control: the short `ui.label` when the design gives one. */
export function fieldLabel(field) {
  return field?.ui?.label || field?.label || ''
}

const WIDE_TYPES = new Set(['image-editor', 'rich-text', 'textarea', 'string-list', 'segmented'])
const WIDE_CONTROLS = new Set(['thumbnail-grid', 'brand-identity', 'image-picker'])

export function isWideField(field) {
  const ui = field?.ui || {}
  if (WIDE_CONTROLS.has(ui.control)) return true
  if (ui.width === 'full') return true
  if (ui.width === 'half') return false
  return WIDE_TYPES.has(field?.type)
}
